import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { SourceTracedHero } from "./source-traced-hero";
import { MobileHero } from "./mobile/mobile-hero";
import { ScrollCinematic } from "@/components/vision/reveal";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

function useCountUp(target: number, durationMs = 1800) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / durationMs);
          setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, durationMs]);
  return [val, ref] as const;
}

function EditorialStat({ to, suffix = "", label, last = false }: { to: number; suffix?: string; label: string; last?: boolean }) {
  const [v, ref] = useCountUp(to);
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "it-IT";
  return (
    <div className={`flex flex-col gap-2 px-6 py-8 sm:px-8 ${last ? "" : "md:border-r md:border-white/10"}`}>
      <div className="font-editorial text-4xl font-bold tracking-[-0.01em] text-white sm:text-5xl">
        <span ref={ref}>{v.toLocaleString(locale)}</span>
        <span className="text-teal-400">{suffix}</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
    </div>
  );
}

export function HeroSection() {
  const { t } = useTranslation();
  return (
    <>
      <div className="md:hidden"><MobileHero /></div>
      <section className="relative isolate overflow-hidden bg-[#0a0a0c] pt-32 hidden md:block">
        {/* Subtle editorial glow — restrained, not an aurora mesh */}
        <div className="pointer-events-none absolute -top-1/4 left-[15%] h-[520px] w-[520px] rounded-full bg-teal-500/[0.06] blur-[130px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-white/10" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          {/* Lead story: asymmetric editorial hero */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 gap-12 border-b border-white/10 py-20 lg:grid-cols-12"
          >
            <div className="lg:col-span-8">
              <span className="mb-8 inline-block border border-teal-500/30 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.22em] text-teal-400">
                {t("hero.kicker")}
              </span>
              <h1 className="font-editorial text-5xl font-bold leading-[0.95] tracking-[-0.01em] text-white text-balance md:text-7xl lg:text-[5.25rem]">
                {t("hero.titleA")}{" "}
                <span className="italic font-normal text-teal-400">{t("hero.titleB")}</span>
              </h1>

              <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end">
                <p className="max-w-md text-[17px] font-light leading-relaxed text-white/60">
                  {t("hero.subtitle")}
                </p>
                <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                  <Link to="/signup" className="aurora-button">
                    {t("hero.ctaPrimary")} <ArrowRight className="h-4 w-4" />
                  </Link>
                  <button type="button" onClick={() => openWhatsAppContact()} className="ghost-button">
                    {t("hero.ctaSecondary")}
                  </button>
                </div>
              </div>
            </div>

            {/* Editorial pull-quote card */}
            <div className="flex flex-col justify-end lg:col-span-4">
              <figure className="border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm">
                <blockquote className="font-editorial text-lg italic leading-relaxed text-white/70">
                  &ldquo;{t("hero.quote")}&rdquo;
                </blockquote>
                <div className="my-5 h-px w-12 bg-teal-500" />
                <figcaption className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  {t("hero.quoteAttr")}
                </figcaption>
              </figure>
            </div>
          </motion.div>

          {/* Data band — hairline ruled */}
          <div className="grid grid-cols-2 border-b border-white/10 md:grid-cols-4">
            <EditorialStat to={5} suffix="M+" label={t("hero.stats.sources")} />
            <EditorialStat to={73} suffix="%" label={t("hero.stats.time")} />
            <EditorialStat to={1500} suffix="+" label={t("hero.stats.pros")} />
            <EditorialStat to={60} suffix="s" label={t("hero.stats.draft")} last />
          </div>

          {/* Source-traced proof — first-class credibility element */}
          <ScrollCinematic className="relative py-20">
            <div className="mb-8 flex items-center gap-4">
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-teal-400">01</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/45">
                {t("hero.badge")}
              </span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 48, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <SourceTracedHero />
            </motion.div>
          </ScrollCinematic>
        </div>
      </section>
    </>
  );
}
